import {
  Box,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  // ListItemTextProps,
  Typography,
  useTheme,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { ReactNode } from 'react';

import { Card, RouterLink } from 'components';
import strings from 'constants/index';
import { normalizeProperty } from 'utils/text.utils';

import { CardProps } from './../Card/Card';
import { propertiesCardStyles } from './PropertiesCard.styles';

export interface PropertyCardItemProps {
  value: ReactNode; // Changed from Pick<ListItemTextProps, 'primary'> to ReactNode
  name?: string;
}

// Define MetaProperty interface to fix the metaProperties typing issue
interface MetaProperty {
  name: string;
  value: ReactNode; // Using ReactNode instead of the problematic Pick type
}

interface PropertyCardActions {
  icon: JSX.Element;
  onClick: (evt?: any) => void;
  disabled?: boolean;
}

export interface PropertyCardEditProps {
  editComponent?: JSX.Element;
  actions?: Array<PropertyCardActions>;
  isEditing?: boolean;
  isEdited?: boolean;
  isEditDisabled?: boolean;
  link?: string;
  sx?: any;
  valueAlias?: string;
}

export interface PropertiesCardProps extends CardProps {
  title?: string | JSX.Element;
  properties?: Array<PropertyCardItemProps & PropertyCardEditProps>;
  metaProperties?: Array<MetaProperty & PropertyCardEditProps>;
  children?: JSX.Element;
  dense?: boolean;
  listTestId?: string;
}

const PropertiesCard = ({
  title,
  properties = [],
  metaProperties = [],
  children,
  dense = true,
  listTestId = 'properties-list',
  ...cardProps
}: PropertiesCardProps) => {
  const theme = useTheme();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const isEmbedded = searchParams.get('isEmbedded') === 'true';

  const renderEditActions = (actions?: Array<PropertyCardActions | false | null | undefined>) => {
    const visibleActions = (actions || []).filter(
      (action): action is PropertyCardActions => !!action && !!action.icon,
    );
    if (!visibleActions.length) {
      return null;
    }
    return (
      <>
        {visibleActions.map(({ icon, onClick, disabled }, idx) => (
          <IconButton
            data-testid="edit-action"
            disabled={disabled}
            key={idx}
            {...(onClick && { onClick })}
            size="large"
          >
            {icon}
          </IconButton>
        ))}
      </>
    );
  };

  const hasItems = properties.length || metaProperties.length || children;
  return (
    <Card
      data-testid="properties-card"
      title={title}
      {...cardProps}
      contentClasses={hasItems ? 'nonPaddedContent' : ''}
    >
      {!hasItems ? (
        strings.no_items_avalable
      ) : (
        <List dense={dense} data-testid={listTestId}>
          {properties.map(
            ({ value, name, link, isEditing, isEdited, editComponent, actions, sx }, idx) => (
              <ListItem
                data-testid={`${name}-property`}
                key={name || (typeof value === 'string' && value) || idx}
                divider={idx !== properties.length - 1}
                secondaryAction={renderEditActions(actions)}
                sx={{
                  ...sx,
                  ...propertiesCardStyles.listItem,
                  pr: `${(actions?.filter(Boolean)?.length || 0) * 48 + Number(theme.spacing(5).slice(0, -2))}px` || 4,
                }}
              >
                {isEditing ? (
                  <Box flexGrow={1} my="2px" display="flex" alignItems="center">
                    {editComponent}
                  </Box>
                ) : (
                  <ListItemText
                    primary={
                      <>
                        <Typography color="textSecondary">{name}</Typography>
                        {link && value ? (
                          <RouterLink target={isEmbedded ? '_blank' : '_self'} to={link}>
                            {value}
                          </RouterLink>
                        ) : value || value === 0 ? (
                          value
                        ) : (
                          strings.not_applicable
                        )}
                      </>
                    }
                    primaryTypographyProps={{
                      variant: 'body1',
                      sx: isEdited ? propertiesCardStyles.isEdited : {},
                    }}
                  />
                )}
              </ListItem>
            ),
          )}
          {metaProperties.map(
            (
              { name, value, link, editComponent, isEdited, isEditing, actions, valueAlias },
              idx,
            ) => (
              <ListItem
                data-testid={`${name}-metaproperty`}
                key={name}
                divider={idx !== metaProperties.length - 1}
                secondaryAction={renderEditActions(actions)}
                sx={{
                  ...propertiesCardStyles.listItem,
                  pr: `${(actions?.filter(Boolean)?.length || 0) * 48 + Number(theme.spacing(5).slice(0, -2))}px` || 4,
                }}
              >
                {isEditing ? (
                  <Box flexGrow={1} my="2px" display="flex" alignItems="center">
                    {editComponent}
                  </Box>
                ) : (
                  <ListItemText
                    primary={
                      <>
                        <Typography color="textSecondary">{normalizeProperty(name)}</Typography>
                        {link && value ? (
                          <Link
                            target="_blank"
                            href={typeof value === 'string' ? value : '#'}
                            rel="noreferrer"
                          >
                            {value}
                          </Link>
                        ) : isEdited ? (
                          value || strings.not_applicable
                        ) : (
                          valueAlias || value || strings.not_applicable
                        )}
                      </>
                    }
                    primaryTypographyProps={{
                      variant: 'body1',
                      sx: isEdited ? propertiesCardStyles.isEdited : {},
                    }}
                  />
                )}
              </ListItem>
            ),
          )}
        </List>
      )}
      {children}
    </Card>
  );
};

export default PropertiesCard;
